/**
 * SynPop semantic color tokens — JS / hex values for charts (recharts SVG fill/stroke).
 *
 * For Tailwind classes use bg-positive / text-negative-soft etc. (see tailwind.config.js).
 * For HSL CSS vars see index.css :root and .dark blocks.
 *
 * If you change a hex here, update the corresponding HSL var in index.css too.
 */

export const SEMANTIC_HEX = {
  // Positive — soft teal
  positive: '#0d9488',          // teal-600 (light mode default — strong)
  positiveSoft: '#5eead4',      // teal-300 (muted variant for chart fills)
  positiveBg: '#f0fdfa',        // teal-50 (panel bg)
  positiveDark: '#2dd4bf',      // teal-400 (dark-mode default)

  // Negative — soft coral
  negative: '#f87171',          // coral (red-400 — softer than red-600)
  negativeSoft: '#fda4af',      // rose-300 (muted coral)
  negativeBg: '#fff1f2',        // rose-50 (panel bg)
  negativeDark: '#fb7185',      // rose-400 (dark-mode default)

  // Warning — amber
  warning: '#f59e0b',           // amber-500
  warningSoft: '#fcd34d',       // amber-300
  warningBg: '#fef3c7',         // amber-100
  warningDark: '#fbbf24',       // amber-400

  // Info / brand — blue
  info: '#2563eb',              // blue-600 (= --primary)
  infoSoft: '#93c5fd',          // blue-300
  infoBg: '#eff6ff',            // blue-50
  infoDark: '#60a5fa',          // blue-400

  // Neutral — slate
  neutral: '#64748b',           // slate-500
  neutralSoft: '#cbd5e1',       // slate-300
  neutralBg: '#f8fafc',         // slate-50
  neutralDark: '#94a3b8',       // slate-400
};

/**
 * Sentiment chart colors — used by Likert/diverging charts.
 * Strong tones at extremes, softer mid-tones for adjacent options.
 */
export const SENTIMENT_HEX = {
  strongly_negative: SEMANTIC_HEX.negative,        // coral
  negative: SEMANTIC_HEX.negativeSoft,             // soft coral
  neutral: SEMANTIC_HEX.neutral,                   // slate
  positive: SEMANTIC_HEX.positiveSoft,             // soft teal
  strongly_positive: SEMANTIC_HEX.positive,        // teal
};

/**
 * 5-option scale resolver: index 0 = strongly negative, last = strongly positive.
 * Falls back to neutral for indices that don't fit a sentiment.
 */
export function getSentimentHex(sentiment, index, total) {
  if (sentiment === 'negative') {
    return index === 0 && total >= 4 ? SENTIMENT_HEX.strongly_negative : SENTIMENT_HEX.negative;
  }
  if (sentiment === 'positive') {
    return index === total - 1 && total >= 4 ? SENTIMENT_HEX.strongly_positive : SENTIMENT_HEX.positive;
  }
  return SENTIMENT_HEX.neutral;
}

/**
 * Delta-color ramp: stronger color the more extreme the delta.
 */
export function getDeltaHex(delta) {
  if (delta >= 0.3) return SENTIMENT_HEX.strongly_positive;
  if (delta > 0) return SENTIMENT_HEX.positive;
  if (delta > -0.3) return SENTIMENT_HEX.negative;
  return SENTIMENT_HEX.strongly_negative;
}
